import {Component, OnInit} from '@angular/core';
import {classToClass} from 'class-transformer';
import {Filter} from '../../../interfaces/filter.interface';
import {IMyDrpOptions} from 'mydaterangepicker';
import * as moment from 'moment';
import {saveAs} from 'file-saver';
import {ApiService} from '../../../shared/services/api.service';
import {ElectricMeterCurrentLoss} from '../../../entities/electric-meter-current-loss';

@Component({
  selector: 'app-current-loss',
  templateUrl: './current-loss.component.html',
  styles: []
})
export class CurrentLossComponent implements OnInit {

  public meterreadings: ElectricMeterCurrentLoss[] = [];
  public filteredMeterreadings: ElectricMeterCurrentLoss[] = [];

  public marketNameFilter = '';
  public branchFilter: Filter;
  public internalNumberFilter = '';

  public dateRangePickerOptions: IMyDrpOptions;
  public dateRangeModel;
  public loading = false;
  public dateChanged = false;

  constructor(private apiService: ApiService) {
    this.loading = true;
    this.initFilters();
    this.initDateRangePicker();
    this.load().then(() => {
      this.applyFilters();
      this.loading = false;
    }).catch((error) => {
      console.log('Could not load data. ', error);
      this.loading = false;
    });
  }

  ngOnInit() {
  }

  /**
   * Load data from api. Promise resolves, if loading is done
   */
  load() {
    return new Promise((resolve, reject) => {
      const startDate = this.dateRangeModel.beginDate.year + '-' + this.dateRangeModel.beginDate.month.toString().padStart(2, '0') + '-'
        + this.dateRangeModel.beginDate.day.toString().padStart(2, '0');
      const endDate = this.dateRangeModel.endDate.year + '-' + this.dateRangeModel.endDate.month.toString().padStart(2, '0') + '-'
        + this.dateRangeModel.endDate.day.toString().padStart(2, '0');
      this.apiService.find<ElectricMeterCurrentLoss>(ElectricMeterCurrentLoss,
        '/backoffice/evaluation-electricity-loss/' + startDate + '/' + endDate)
        .subscribe((result) => {
          // Sort results by date ASC
          result.sort((a, b) => {
            const dateA = new Date(a.marketdateEvasiveDate);
            const dateB = new Date(b.marketdateEvasiveDate);
            if (dateA > dateB) { return -1; }
            if (dateA < dateB) { return 1; }
            return 0;
          });

          // Only show meterreadings with a minimum difference - MGE001-18797
          this.meterreadings = result.filter((value) => value.difference < -3)
            .filter(item => item.electricmeterFixedAmount === '0');
          this.filteredMeterreadings = result.filter((value) => value.difference < -3);
          resolve();
        }, (error) => reject(error));
    });
  }

  initDateRangePicker() {
    const startDate = moment();
    startDate.subtract(35, 'days');
    const endDate = moment();
    this.dateRangePickerOptions = {
      dayLabels: {su: 'So', mo: 'Mon', tu: 'Di', we: 'Mi', th: 'Do', fr: 'Fr', sa: 'Sa'},
      monthLabels: { 1: 'Jan', 2: 'Feb', 3: 'Mrz', 4: 'Apr', 5: 'Mai', 6: 'Jun', 7: 'Jul', 8: 'Aug', 9: 'Sep', 10: 'Okt',
        11: 'Nov', 12: 'Dez' },
      dateFormat: 'dd.mm.yyyy',
      yearSelector: false,
      monthSelector: false,
      selectBeginDateTxt: 'Startdatum auswählen',
      selectEndDateTxt: 'Enddatum auswählen',
      showClearBtn: true,
      showApplyBtn: true,
      openSelectorOnInputClick: true,
      height: '26px',
      width: '239px'
    };
    this.dateRangeModel = {
      beginDate: {
        year: startDate.year(),
        month: startDate.month() + 1,
        day: startDate.date()
      },
      endDate: {
        year: endDate.year(),
        month: endDate.month() + 1,
        day: endDate.date()
      }
    };
  }

  async initFilters() {
    this.branchFilter = {
      label: 'Niederlassung',
      options: [{ label: 'Alle', value: 'all' }],
      selected: {
        label: 'Alle',
        value: 'all'
      }
    };

    const branches = await this.apiService.getBranches();
    branches.forEach(branch => {
      this.branchFilter.options.push({
        value: branch.name,
        label: branch.name
      });
    });
  }

  changeFilter(filter: Filter, option: any) {
    filter.selected = option;
    this.applyFilters();
  }

  /**
   * Applies all filters (if set)
   */
  applyFilters() {
    let tmp = this.meterreadings;

    // Apply branch name filter
    if (this.branchFilter.selected.value !== 'all') {
      tmp = tmp.filter((item) => item.branchName === this.branchFilter.selected.value);
    }

    // Apply internal number filter
    if (this.internalNumberFilter && this.internalNumberFilter !== '') {
      tmp = tmp.filter((mr) => mr.electricmeterInternalNumber.includes(this.internalNumberFilter));
    }

    // Apply market name filter
    if (this.marketNameFilter && this.marketNameFilter !== '') {
      if (tmp.some((item) => item.marketdayNumber.includes(this.marketNameFilter))) {
        tmp = tmp.filter((mr) => mr.marketdayNumber.includes(this.marketNameFilter));
      } else {
        tmp = tmp.filter((mr) => mr.marketdayName.includes(this.marketNameFilter));
      }
    }

    // Apply filter result
    this.filteredMeterreadings = tmp;
  }

  /**
   * Reloads the data, re-initializes & applies the filters
   */
  reloadAndFilter() {
    this.load().then(() => {
      this.initFilters();
      this.applyFilters();
    }).catch((error) => {
      console.log('Could not load data. ', error);
    });
  }

  onActivate(event: any) {}
  onSelect(event: any) {}

  /**
   * Saves the filtered meterreadings as csv file
   */
  saveToCSV() {
    const headers = [
      'Datum Marktabrechnung',
      'Marktnummer',
      'Marktplatz',
      'Niederlassung',
      'Zählernummer',
      'Interne Nummer',
      'Standort',
      'Ablesewert letzte Marktschließung kWh',
      'Ablesewert heute Marktöffnung kWh',
      'Stromschwund in kWh'
    ];
    let body = headers.join(';') + '\r\n';

    this.filteredMeterreadings.forEach(
      (entry) => {
        body = body.concat(entry.marketdateEvasiveDate, ';');
        body = body.concat(entry.marketdayNumber, ';');
        body = body.concat(entry.marketdayName, ';');
        body = body.concat(entry.branchName, ';');
        body = body.concat(entry.electricmeterNumber, ';');
        body = body.concat(entry.electricmeterInternalNumber, ';');
        body = body.concat(entry.electricmeterLocation, ';');
        body = body.concat(entry.meterreadingAfterValue + '', ';');
        body = body.concat(entry.meterreadingBeforeValue + '', ';');
        body = body.concat(entry.difference + '', ';\r\n');
      }
    );
    const blob = new Blob([body], {type: 'text/plain;charset=utf-8'});
    saveAs(blob, 'Übersicht_Stromverlust.csv');
  }

}



// WEBPACK FOOTER //
// ./src/app/evaluation/components/current-loss/current-loss.component.ts